import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

function obtenerFechaBase(item) {
  return item.Fecha || item.FechaTexto || item.FechaCreacion || '';
}

function obtenerFechaParte(valor) {
  if (!valor) return '';

  return String(valor).substring(0, 10);
}

function fechaLocalTexto(fecha = new Date()) {
  return [
    fecha.getFullYear(),
    String(fecha.getMonth() + 1).padStart(2, '0'),
    String(fecha.getDate()).padStart(2, '0')
  ].join('-');
}

function formatearSoloFecha(valor) {
  const fechaParte = obtenerFechaParte(valor);

  if (!fechaParte) return '-';

  const [anio, mes, dia] = fechaParte.split('-');

  if (!anio || !mes || !dia) return '-';

  return `${dia}/${mes}/${anio}`;
}

function obtenerCodigoMoneda(moneda) {
  const texto = String(moneda || '').toUpperCase();

  if (
    texto.includes('USD') ||
    texto.includes('DOLAR') ||
    texto.includes('DÓLAR')
  ) {
    return 'USD';
  }

  return 'MXN';
}

function formatearMoneda(valor, moneda = 'MXN') {
  return Number(valor || 0).toLocaleString('es-MX', {
    style: 'currency',
    currency: moneda,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function obtenerEstatus(item) {
  if (item.Cancelado) return 'Cancelado';

  if (item.PdfFirmadoPath) return 'Pagado';

  if (String(item.Estatus || '').toLowerCase() === 'pendiente') {
    return 'Pendiente';
  }

  return 'Emitido';
}

function esCancelado(item) {
  return obtenerEstatus(item).toLowerCase() === 'cancelado';
}

function importeNumerico(item) {
  return Number(item.Importe || 0);
}

function tarjetaStyle(tipo = 'normal') {
  const base = {
    cursor: 'pointer',
    minHeight: '82px',
    transition: 'all 0.15s ease',
    borderLeft: '4px solid transparent',
    padding: '12px 14px'
  };

  if (tipo === 'pendiente') {
    return {
      ...base,
      background: '#fff8db',
      borderLeftColor: '#f0ad00'
    };
  }

  if (tipo === 'pagado') {
    return {
      ...base,
      background: '#eaf8ef',
      borderLeftColor: '#198754'
    };
  }

  if (tipo === 'cancelado') {
    return {
      ...base,
      background: '#fdecec',
      borderLeftColor: '#dc3545'
    };
  }

  if (tipo === 'emitido') {
    return {
      ...base,
      background: '#eef4ff',
      borderLeftColor: '#2563eb'
    };
  }

  if (tipo === 'importe') {
    return {
      ...base,
      background: '#eef4ff',
      borderLeftColor: '#2563eb'
    };
  }

  return {
    ...base,
    background: '#ffffff',
    borderLeftColor: '#6b7280'
  };
}

function DashboardKpiCard({
  titulo,
  valor,
  tipo = 'normal',
  title,
  onClick
}) {
  return (
    <div
      className="card-box dashboard-kpi-card"
      style={tarjetaStyle(tipo)}
      title={title || String(valor ?? '')}
      onClick={onClick}
    >
      <div
        className="dashboard-kpi-label"
        style={{ fontSize: '12px', marginBottom: '6px' }}
      >
        {titulo}
      </div>

      <div
        className="dashboard-kpi-value"
        style={{ fontSize: '22px', lineHeight: 1.12 }}
      >
        {valor}
      </div>
    </div>
  );
}

function DashboardPage() {
  const navigate = useNavigate();

  const [recibos, setRecibos] = useState([]);
  const [mensaje, setMensaje] = useState('');
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    cargarDashboard();
  }, []);

  async function cargarDashboard() {
    try {
      setCargando(true);
      setMensaje('');

      const res = await api.get('/recibos');

      setRecibos(res.data || []);
    } catch (error) {
      setMensaje(error.response?.data?.mensaje || error.message);
    } finally {
      setCargando(false);
    }
  }

  const hoyTexto = fechaLocalTexto();
  const mesActualTexto = hoyTexto.substring(0, 7);

  const recibosHoy = useMemo(() => {
    return recibos.filter((item) =>
      !esCancelado(item) &&
      obtenerFechaParte(obtenerFechaBase(item)) === hoyTexto
    );
  }, [recibos, hoyTexto]);

  const recibosMes = useMemo(() => {
    return recibos.filter((item) =>
      !esCancelado(item) &&
      obtenerFechaParte(obtenerFechaBase(item)).substring(0, 7) === mesActualTexto
    );
  }, [recibos, mesActualTexto]);

  const resumen = useMemo(() => {
    return recibos.reduce((acc, item) => {
      const estatus = obtenerEstatus(item);
      const estatusNormalizado = estatus.toLowerCase();
      const codigoMoneda = obtenerCodigoMoneda(item.Moneda);
      const esDelMes =
        obtenerFechaParte(obtenerFechaBase(item)).substring(0, 7) === mesActualTexto;

      if (esDelMes && !esCancelado(item)) {
        if (codigoMoneda === 'USD') {
          acc.ImporteMesUSD += importeNumerico(item);
        } else {
          acc.ImporteMesMXN += importeNumerico(item);
        }
      }

      if (estatusNormalizado === 'emitido') acc.Emitidos += 1;
      if (estatusNormalizado === 'pendiente') acc.Pendientes += 1;
      if (estatusNormalizado === 'pagado') acc.Pagados += 1;
      if (estatusNormalizado === 'cancelado') acc.Cancelados += 1;

      return acc;
    }, {
      ImporteMesMXN: 0,
      ImporteMesUSD: 0,
      Emitidos: 0,
      Pendientes: 0,
      Pagados: 0,
      Cancelados: 0
    });
  }, [recibos, mesActualTexto]);

  const porEmpresa = useMemo(() => {
    const mapa = new Map();

    recibos.forEach((item) => {
      const empresa = item.EmpresaEmite || item.PersonaEmite || 'Sin empresa';

      if (!mapa.has(empresa)) {
        mapa.set(empresa, {
          Empresa: empresa,
          Cantidad: 0,
          ImporteMXN: 0,
          ImporteUSD: 0,
          Emitidos: 0,
          Pendientes: 0,
          Pagados: 0,
          Cancelados: 0
        });
      }

      const fila = mapa.get(empresa);
      const codigoMoneda = obtenerCodigoMoneda(item.Moneda);
      const estatus = obtenerEstatus(item);

      fila.Cantidad += 1;

      if (!esCancelado(item)) {
        if (codigoMoneda === 'USD') {
          fila.ImporteUSD += importeNumerico(item);
        } else {
          fila.ImporteMXN += importeNumerico(item);
        }
      }

      if (estatus === 'Emitido') fila.Emitidos += 1;
      if (estatus === 'Pendiente') fila.Pendientes += 1;
      if (estatus === 'Pagado') fila.Pagados += 1;
      if (estatus === 'Cancelado') fila.Cancelados += 1;
    });

    return Array.from(mapa.values())
      .sort((a, b) => b.Cantidad - a.Cantidad || a.Empresa.localeCompare(b.Empresa));
  }, [recibos]);

  const ultimosRecibos = useMemo(() => {
    return [...recibos]
      .sort((a, b) => {
        const fechaA = new Date(obtenerFechaBase(a)).getTime() || 0;
        const fechaB = new Date(obtenerFechaBase(b)).getTime() || 0;

        return fechaB - fechaA;
      })
      .slice(0, 10);
  }, [recibos]);

  return (
    <div
      className="page-container-normal"
      style={{ maxWidth: '1140px' }}
    >

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 className="mb-1">Dashboard</h1>

          <div className="text-muted">
            Resumen ejecutivo con datos reales de recibos
          </div>
        </div>

        <div className="d-flex gap-2">
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => navigate('/recibos')}
          >
            Nuevo recibo
          </button>

          <button
            className="btn btn-outline-secondary"
            type="button"
            onClick={cargarDashboard}
          >
            Actualizar
          </button>
        </div>
      </div>

      {mensaje && (
        <div className="alert alert-info">
          {mensaje}
        </div>
      )}

      {cargando && (
        <div className="alert alert-info">
          Cargando dashboard...
        </div>
      )}

      <div className="mb-3">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '18fr 18fr 32fr 32fr',
            gap: '10px',
            marginBottom: '10px'
          }}
        >
          <DashboardKpiCard
            titulo="Recibos hoy"
            valor={recibosHoy.length}
            title="Ver recibos emitidos hoy"
            onClick={() => navigate('/historico?hoy=1')}
          />

          <DashboardKpiCard
            titulo="Recibos del mes"
            valor={recibosMes.length}
            title="Ver recibos del mes"
            onClick={() => navigate('/historico?mesActual=1')}
          />

          <DashboardKpiCard
            titulo="Importe mes MXN"
            valor={formatearMoneda(resumen.ImporteMesMXN, 'MXN')}
            tipo="importe"
            title="Ver recibos MXN del mes"
            onClick={() => navigate('/historico?mesActual=1&monedaTexto=MXN')}
          />

          <DashboardKpiCard
            titulo="Importe mes USD"
            valor={formatearMoneda(resumen.ImporteMesUSD, 'USD')}
            tipo="importe"
            title="Ver recibos USD del mes"
            onClick={() => navigate('/historico?mesActual=1&monedaTexto=USD')}
          />
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: '10px'
          }}
        >
          <DashboardKpiCard
            titulo="Emitidos"
            valor={resumen.Emitidos}
            tipo="emitido"
            title="Ver recibos emitidos"
            onClick={() => navigate('/historico?estatus=Emitido')}
          />

          <DashboardKpiCard
            titulo="Pendientes"
            valor={resumen.Pendientes}
            tipo="pendiente"
            title="Ver recibos pendientes"
            onClick={() => navigate('/historico?estatus=Pendiente')}
          />

          <DashboardKpiCard
            titulo="Pagados"
            valor={resumen.Pagados}
            tipo="pagado"
            title="Ver recibos pagados"
            onClick={() => navigate('/historico?estatus=Pagado')}
          />

          <DashboardKpiCard
            titulo="Cancelados"
            valor={resumen.Cancelados}
            tipo="cancelado"
            title="Ver recibos cancelados"
            onClick={() => navigate('/historico?estatus=Cancelado')}
          />
        </div>
      </div>

      <div className="card-box mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Por empresa</h5>

          <span className="badge bg-secondary">
            {porEmpresa.length} empresas
          </span>
        </div>

        <div className="table-wrap">
          <table className="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th>Empresa</th>
                <th className="text-center">Recibos</th>
                <th style={{ textAlign: 'right' }}>Importe MXN</th>
                <th style={{ textAlign: 'right' }}>Importe USD</th>
                <th className="text-center">Emitidos</th>
                <th className="text-center">Pendientes</th>
                <th className="text-center">Pagados</th>
                <th className="text-center">Cancelados</th>
              </tr>
            </thead>

            <tbody>
              {porEmpresa.map((item) => (
                <tr key={item.Empresa}>
                  <td>
                    <strong>{item.Empresa}</strong>
                  </td>

                  <td className="text-center">
                    {item.Cantidad}
                  </td>

                  <td style={{ textAlign: 'right' }}>
                    {formatearMoneda(item.ImporteMXN, 'MXN')}
                  </td>

                  <td style={{ textAlign: 'right' }}>
                    {formatearMoneda(item.ImporteUSD, 'USD')}
                  </td>

                  <td className="text-center">
                    {item.Emitidos}
                  </td>

                  <td className="text-center">
                    {item.Pendientes}
                  </td>

                  <td className="text-center">
                    {item.Pagados}
                  </td>

                  <td className="text-center">
                    {item.Cancelados}
                  </td>
                </tr>
              ))}

              {porEmpresa.length === 0 && (
                <tr>
                  <td
                    colSpan="8"
                    className="text-center text-muted py-4"
                  >
                    Sin recibos para agrupar.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card-box">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Últimos recibos</h5>

          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() => navigate('/historico')}
          >
            Ver histórico
          </button>
        </div>

        <div className="table-wrap">
          <table className="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th>Folio</th>
                <th>Fecha</th>
                <th>Empresa</th>
                <th>Recibe</th>
                <th style={{ textAlign: 'right' }}>Importe</th>
                <th style={{ textAlign: 'center' }}>Estatus</th>
                <th>Detalle</th>
              </tr>
            </thead>

            <tbody>
              {ultimosRecibos.map((item) => {
                const estatus = obtenerEstatus(item);

                return (
                  <tr key={item.Id}>
                    <td>
                      <strong>{item.Folio}</strong>
                    </td>

                    <td>
                      {formatearSoloFecha(obtenerFechaBase(item))}
                    </td>

                    <td>
                      {item.EmpresaEmite || item.PersonaEmite || '-'}
                    </td>

                    <td>
                      {item.EmpresaRecibe || item.PersonaRecibe || '-'}
                    </td>

                    <td style={{ textAlign: 'right' }}>
                      {formatearMoneda(
                        item.Importe,
                        obtenerCodigoMoneda(item.Moneda)
                      )}
                    </td>

                    <td style={{ textAlign: 'center' }}>
                      <span
                        className={
                          estatus === 'Cancelado'
                            ? 'badge bg-danger'
                            : estatus === 'Pagado'
                              ? 'badge bg-success'
                              : estatus === 'Pendiente'
                                ? 'badge bg-warning text-dark'
                                : 'badge bg-primary'
                        }
                      >
                        {estatus}
                      </span>
                    </td>

                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        type="button"
                        onClick={() => navigate(`/recibos/${item.Id}`)}
                      >
                        Detalle
                      </button>
                    </td>
                  </tr>
                );
              })}

              {ultimosRecibos.length === 0 && (
                <tr>
                  <td
                    colSpan="7"
                    className="text-center text-muted py-4"
                  >
                    Sin recibos recientes.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}

export default DashboardPage;
